import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '../utils/api'

export const useTMSProjects = () => {
  const queryClient = useQueryClient()

  // Fetch user's TMS projects
  const {
    data: projectsData,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['tms-projects'],
    queryFn: async () => {
      const response = await api.get('/tms/user/projects')
      return response.data
    },
    retry: 2
  })

  // Add new project
  const addProjectMutation = useMutation({
    mutationFn: async (projectData) => {
      const response = await api.post('/tms/user/projects', projectData)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tms-projects'] })
    }
  })

  // Update project
  const updateProjectMutation = useMutation({
    mutationFn: async ({ projectId, updateData }) => {
      const response = await api.put(`/tms/user/projects/${projectId}`, updateData)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tms-projects'] })
    }
  })

  // Delete project
  const deleteProjectMutation = useMutation({
    mutationFn: async (projectId) => {
      const response = await api.delete(`/tms/user/projects/${projectId}`)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tms-projects'] })
    }
  })

  // Set default project
  const setDefaultMutation = useMutation({
    mutationFn: async (projectId) => {
      const response = await api.put(`/tms/user/projects/${projectId}/default`)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tms-projects'] })
    }
  })

  // Import job to TMS project
  const importJobMutation = useMutation({
    mutationFn: async ({ projectId, importData }) => {
      const response = await api.post(`/tms/user/projects/${projectId}/import`, importData)
      return response.data
    }
  })

  // Test connection with project-specific credentials
  const testConnectionMutation = useMutation({
    mutationFn: async ({ projectId, apiKey }) => {
      // Test connection by temporarily calling the backend with project credentials
      // We'll create a temporary user settings object to test the connection
      const response = await api.post('/tms/test-connection-with-credentials', {
        project_id: projectId,
        api_key: apiKey,
        provider: 'phrase'
      })
      return response.data
    }
  })

  const projects = projectsData?.projects || []
  const defaultProjectId = projectsData?.default_project_id

  return {
    // Data
    projects,
    defaultProjectId,
    isLoading,
    error,

    // Actions
    refetch,
    addProject: addProjectMutation.mutate,
    updateProject: updateProjectMutation.mutateAsync,
    deleteProject: deleteProjectMutation.mutate,
    setDefaultProject: setDefaultMutation.mutate,
    importJob: importJobMutation.mutate,
    testConnection: testConnectionMutation.mutateAsync,

    // Mutation states
    isAddingProject: addProjectMutation.isPending,
    isUpdatingProject: updateProjectMutation.isPending,
    isDeletingProject: deleteProjectMutation.isPending,
    isSettingDefault: setDefaultMutation.isPending,
    isImportingJob: importJobMutation.isPending,
    isTestingConnection: testConnectionMutation.isPending,

    // Errors
    addProjectError: addProjectMutation.error,
    updateProjectError: updateProjectMutation.error,
    deleteProjectError: deleteProjectMutation.error,
    setDefaultError: setDefaultMutation.error,
    importJobError: importJobMutation.error,
    testConnectionError: testConnectionMutation.error
  }
}